import React from 'react';
import { useNavigate } from 'react-router-dom';

interface Section {
  icon: string;
  title: string;
  steps: string[];
}

const PARENT_SECTIONS: Section[] = [
  {
    icon: '🚀',
    title: 'Kidstrack & Subjects',
    steps: [
      'Open the Kidstrack tab and pick a child to manage.',
      'The "Others" tab holds the built-in tools: Daily Summary, Homework, Timetable, Schedule, Math Quiz, Reading and Results.',
      'Use "+ Add Subject" to add up to 6 of your own subjects (English, Science, Social…).',
      'On a subject, upload the child’s syllabus (PDF, DOC or DOCX, up to 10 MB). You can replace it anytime.',
      'Once the syllabus is processed, each topic shows a study guide and a "Take quiz" button.',
    ],
  },
  {
    icon: '📊',
    title: 'Progress & Results',
    steps: [
      'The Home tab shows progress rings per child: Math, Reading, Homework and a combined Subjects ring (one colored arc per subject).',
      'Open "My Results" (or a subject’s Results section) to see every quiz attempt with score and a "Why this is correct" review.',
      'A daily email report is sent to you each evening summarising the day.',
    ],
  },
  {
    icon: '🖼️',
    title: 'Media, Friends & Chat',
    steps: [
      'Use the Image and Video tabs to upload photos/videos and choose who can see them.',
      'Friends & Groups lets you connect with other families and create groups.',
      'Start a chat with a child or group from the chat area; you’ll get a 🔔 notification for new messages.',
    ],
  },
];

const KID_SECTIONS: Section[] = [
  {
    icon: '🚀',
    title: 'Kidstrack & Subjects',
    steps: [
      'Open the Kidstrack tab. The "Others" tab has your Daily Summary, Homework, Timetable, Schedule, Math and Reading.',
      'Your subjects (like Science or English) appear as tabs — tap one to see its study guides.',
      'Tap a topic to read its study guide, then tap "Take quiz" and pick a difficulty.',
      'After a quiz, see your score and a "Why this is correct" explanation for each question.',
    ],
  },
  {
    icon: '📊',
    title: 'Your Progress',
    steps: [
      'The Home tab shows your progress rings — Math, Reading, Homework and a Subjects ring.',
      'Tap "My Results" to look back at all your quiz attempts.',
      'The Activities feed shows everything you’ve done recently.',
    ],
  },
  {
    icon: '🤝',
    title: 'Photos, Videos & Friends',
    steps: [
      'The Image and Video tabs show media shared with you.',
      'Friends & Groups is where you can chat with connected friends — watch for the 🔔 when you have new messages.',
    ],
  },
];

const Help: React.FC<{ mode: 'parent' | 'kid' }> = ({ mode }) => {
  const navigate = useNavigate();
  const sections = mode === 'parent' ? PARENT_SECTIONS : KID_SECTIONS;
  const home = mode === 'parent' ? '/parent/home' : '/kid/home';

  return (
    <div className="space-y-5">
      <div className="kt-card p-5">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-display font-extrabold text-gray-800">📘 Help &amp; Instructions</h1>
          <button onClick={() => navigate(home)} className="text-sm text-brand-blue font-bold">← Back</button>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          A quick guide to using KidsTrack. {mode === 'parent' ? 'You manage subjects, syllabi and see each child’s progress.' : 'Learn, take quizzes and track your progress.'}
        </p>
      </div>

      {sections.map((s) => (
        <section key={s.title} className="kt-card p-5">
          <h2 className="font-display font-bold text-gray-800 mb-3 flex items-center gap-2">
            <span className="text-xl">{s.icon}</span> {s.title}
          </h2>
          <ol className="space-y-2 list-decimal list-inside">
            {s.steps.map((step, i) => (
              <li key={i} className="text-sm text-gray-600 leading-relaxed">{step}</li>
            ))}
          </ol>
        </section>
      ))}

      <div className="kt-card p-4 text-center">
        <p className="text-xs text-gray-400">
          More detailed help is on the way. Need something now? Contact your administrator.
        </p>
      </div>
    </div>
  );
};

export default Help;
